import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    Box,
    Container,
    Grid2 as Grid,
    Typography,
    useTheme,
} from "@mui/material";
import { AccountCircleRounded } from "@mui/icons-material";
import { connect, useSelector } from "react-redux";
import { media_AddImage, media_getCatById } from "../../network/api/multimedia.api";
import { user_update, user_updateAvatar } from "../../network/api/user.api";
import { sagaActions } from "../../helper/redux/actions/SagaActions";
import { ErrorContext } from "../../components/GympinPagesProvider";
import { resizeCanvas } from "../../helper/utils";

import _AvatarCropperModal from "./partials/_AvatarCropperModal";
import _AvatarCard from "./partials/_AvatarCard";
import _ProfileForm from "./partials/_ProfileForm";

const EditProfile = (props) => {
    const theme = useTheme();
    const error = useContext(ErrorContext);
    const currentUser = useSelector(state => state.auth.user);
    const [user, setUser] = useState(currentUser);
    const [imageUrl, setImageUrl] = useState("");
    const [imageToCrop, setImageToCrop] = useState(null);
    const [ratio, setRatio] = useState(null);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const cropperRef = useRef(null);

    useEffect(() => {
        document.title = 'ویرایش پروفایل مدیر | جیم‌پین سازمانی';
        setImageUrl(currentUser?.Avatar?.Url || "");
        try {
            if (currentUser?.Username && currentUser?.FullName && currentUser?.Gender && currentUser?.Birthday && currentUser?.NationalCode) {
                props?.introCanGoNext?.(true);
            }
        } catch (e) {}
    }, [currentUser]);

    useEffect(() => {
        props.RequestUser(user);
        getRatio();
    }, []);

    function getRatio() {
        media_getCatById({ id: 2 })
            .then(result => {
                setRatio(result.data.Data);
            })
            .catch(e => {
                const msg = e?.response?.data?.Message || "خطا در دریافت تنظیمات اندازه تصویر";
                error.showError({ message: msg });
            });
    }

    function uploadImage() {
        let canvas = cropperRef.current?.getCanvas();
        if (canvas) {
            if (canvas.height < (ratio?.MINH || 200)) {
                error.showError({ message: "کیفیت و ابعاد تصویر انتخاب‌شده کوچک است." });
                return;
            }
            if (canvas.height > (ratio?.MAXH || 2000)) {
                canvas = resizeCanvas(canvas, ratio?.MAXH || 2000, null);
            }
            setUploadingAvatar(true);
            canvas.toBlob((blob) => {
                if (blob) {
                    setImageToCrop(null);
                    const formData = new FormData();
                    formData.append("MediaType", "IMAGE");
                    formData.append("File", blob);
                    formData.append("CategoryId", "2");
                    formData.append("Title", user?.Username || "avatar");
                    formData.append("Description", user?.Id || "profile");

                    media_AddImage(formData)
                        .then(data => {
                            user_updateAvatar({
                                UserId: currentUser.Id,
                                MultimediaId: data.data.Data.Id,
                            })
                                .then(result => {
                                    setImageUrl(result.data.Data.Avatar ? (result.data.Data.Avatar.Url + "&width=200") : "");
                                    error.showError({ message: "تصویر پروفایل با موفقیت به‌روزرسانی شد." });
                                    props.RequestUser(user);
                                })
                                .catch(e => {
                                    const msg = e?.response?.data?.Message || "خطا در تنظیم تصویر کاربر";
                                    error.showError({ message: msg });
                                });
                        })
                        .catch(e => {
                            const msg = e?.response?.data?.Message || "خطا در بارگذاری فایل تصویر";
                            error.showError({ message: msg });
                        })
                        .finally(() => {
                            setUploadingAvatar(false);
                        });
                } else {
                    setUploadingAvatar(false);
                }
            });
        }
    }

    const handleSelectImage = (e) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader();
            reader.onload = () => {
                setImageToCrop(reader.result);
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const handleSubmitProfile = (values, { setSubmitting }) => {
        if (values?.Bio?.length > 220) {
            error.showError({ message: "متن درباره من طولانی است." });
            setSubmitting(false);
            return;
        }
        if (!values?.FullName) {
            error.showError({ message: "نام و نام خانوادگی الزامی است." });
            setSubmitting(false);
            return;
        }
        if (!values?.Gender) {
            error.showError({ message: "جنسیت الزامی است." });
            setSubmitting(false);
            return;
        }
        if (!values?.Birthday) {
            error.showError({ message: "تاریخ تولد الزامی است." });
            setSubmitting(false);
            return;
        }
        if (!values?.NationalCode) {
            error.showError({ message: "کد ملی الزامی است." });
            setSubmitting(false);
            return;
        }

        user_update(values)
            .then(result => {
                setUser(result.data.Data);
                props.RequestUser(values);
                error.showError({ message: "اطلاعات پروفایل با موفقیت ذخیره شد." });
            })
            .catch(e => {
                const msg = e?.response?.data?.Message || "خطا در به‌روزرسانی پروفایل";
                error.showError({ message: msg });
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Executive Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 3.5,
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 54,
                            height: 54,
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'primary.main',
                            color: '#fff',
                            boxShadow: '0 8px 20px rgba(45, 107, 226, 0.3)',
                        }}
                    >
                        <AccountCircleRounded sx={{ fontSize: 32 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                            پروفایل و مشخصات مدیر
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            ویرایش اطلاعات هویتی، شماره تماس، تصویر نمایه و بیوگرافی مدیر سامانه
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Avatar Column */}
                <Grid size={{ xs: 12, md: 4, lg: 3 }}>
                    <_AvatarCard
                        imageUrl={imageUrl}
                        user={user}
                        uploadingAvatar={uploadingAvatar}
                        onSelectImage={handleSelectImage}
                    />
                </Grid>

                {/* Form Column */}
                <Grid size={{ xs: 12, md: 8, lg: 9 }}>
                    <_ProfileForm
                        user={user}
                        onSubmitProfile={handleSubmitProfile}
                    />
                </Grid>
            </Grid>

            <_AvatarCropperModal
                open={!!imageToCrop}
                onClose={() => setImageToCrop(null)}
                imageToCrop={imageToCrop}
                cropperRef={cropperRef}
                ratio={ratio}
                onConfirmUpload={uploadImage}
            />
        </Container>
    );
};

export default connect(null, sagaActions)(EditProfile);
